import { useEffect, useState } from "react";
import styles from "./track-booking.module.css";
import SideBar from "../../components/sideBar";
import Loader from "../../../admin/components/Loader";
import axios from "axios";
import { authData, withCustomerAuth } from "../../../admin/components/GetAuth";
import dayjs from "dayjs";
import { Alert, Snackbar } from "@mui/material";
import { useWebsite } from "../../../context/WebsiteContext";
import { useSiteBranding } from "../../../hooks/useSiteBranding";

// primaryColor / secondaryColor flow into --primary-color / --secondary-color,
// the same CSS variables the mybooking page reads (with the same fallbacks),
// so both pages stay visually in sync and re-theme together.
function TrackBooking({ primaryColor, secondaryColor }) {
  const auth = authData();
  const [loading, setLoading] = useState(false);
  const [bookigIdNew, setBookigIdNew] = useState("");
  const [bookingData, setBookingData] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState("success");

  const { websiteData } = useWebsite();
  useSiteBranding("Track your booking", websiteData);

  useEffect(() => {
    const getBoockDetails = (tbId) => {
      setLoading(true);

      axios.post(process.env.REACT_APP_API_URL + "v1/customer/tarck-booking-details", { bookId: tbId }, {
        headers: {
          Authorization: `Bearer ${auth?.api_token}`,
        }
      }).then((response) => {
        setLoading(false);
        if (response.data.status === 'ERROR') {
          setSnackbarOpen(true);
          setSnackbarMessage(response.data.message);
          setSnackbarType("error");
        } else {
          setBookingData(response.data.data);
        }
      }).catch((error) => {
        setLoading(false);
      });
    }
    let tempTrackBookId = localStorage.getItem("tempTrackBookId");
    if (tempTrackBookId && auth?.api_token) {
      localStorage.removeItem("tempTrackBookId");
      setBookigIdNew(tempTrackBookId);
      getBoockDetails(tempTrackBookId);
    }
  }, [auth?.api_token]);

  const searchBooking = () => {
    setBookingData(null);
    if (bookigIdNew === '') {
      setSnackbarOpen(true);
      setSnackbarMessage("Please enter bookig Id!");
      setSnackbarType("error");

      return false;
    }

    setLoading(true);

    axios.post(process.env.REACT_APP_API_URL + "v1/customer/tarck-booking-details", { bookId: bookigIdNew }, {
      headers: {
        Authorization: `Bearer ${auth?.api_token}`,
      }
    }).then((response) => {
      setLoading(false);
      if (response.data.status === 'ERROR') {
        setSnackbarOpen(true);
        setSnackbarMessage(response.data.message);
        setSnackbarType("error");
      } else {
        setBookingData(response.data.data);
      }
    }).catch((error) => {
      setLoading(false);
    });
  }

  const statusNum = bookingData ? parseInt(bookingData?.status) : null;

  const statusMeta = () => {
    if (statusNum === -1) return { label: "Cancelled", cls: styles.Cancelled };
    if (statusNum === 0 || statusNum === 1) return { label: "Booked", cls: styles.Booked };
    if (statusNum === 2 || statusNum === 3) return { label: "Picked up", cls: styles.Booked };
    if (statusNum === 4 || statusNum === 9 || statusNum === 5 || statusNum === 6) return { label: "In transit", cls: styles.Transit };
    if (statusNum === 8) return { label: "Partially delivered", cls: styles.Partial };
    if (statusNum === 7) return { label: "Delivered", cls: styles.Delivered };
    return null;
  };

  const meta = statusMeta();

  // find the first not-yet-completed step so it can be marked "Current"
  const firstPendingIndex = bookingData?.trackingDetails?.findIndex((s) => !s?.is_completed);

  const getItemStatusInfo = (item) => {
    if (parseInt(item?.is_deliverred) === 1) {
      return { label: item?.status_text || "Delivered", className: styles.ItemStatusDelivered };
    }
    if (parseInt(item?.is_out_for_delivery) === 1) {
      return { label: item?.status_text || "Out For Delivery", className: styles.ItemStatusTransit };
    }
    if (parseInt(item?.is_ship_received) === 1) {
      return { label: item?.status_text || "Received", className: styles.ItemStatusReceived };
    }
    if (parseInt(item?.is_port_received) === 1) {
      return { label: item?.status_text || "Received At Port", className: styles.ItemStatusTransit };
    }
    return { label: item?.status_text || bookingData?.status_text || "-", className: styles.ItemStatusPending };
  };

  return (<>
    <Loader open={loading} setOpen={setLoading.bind(this)} />

    <div
      className={`${styles.MainDivCustomer}`}
      style={{
        ...(primaryColor ? { "--primary-color": primaryColor } : {}),
        ...(secondaryColor ? { "--secondary-color": secondaryColor } : {}),
      }}
    >
      <div className={`${styles.Container}`}>
        <div className={`${styles.CusRow}`}>
          <div className={`${styles.CustomerSideBar}`}><SideBar /></div>
          <div className={`${styles.CustomerDeSec}`}>
            <div className={`${styles.UserProCard2}`}>
              <div className={`${styles.BookingCard}`}>

                <div className={styles.PageHead}>
                  <p className={styles.PageHeadEyebrow}>Tracking</p>
                  <h1 className={styles.PageHeadTitle}>Track your shipment</h1>
                  <p className={styles.PageHeadSub}>Enter your booking ID to see live order and delivery status.</p>
                </div>

                <div className={styles.SearchCard}>
                  <p className={styles.SearchLabel}>Booking ID</p>
                  <div className={`${styles.SearchSec}`}>
                    <input
                      type="text"
                      placeholder="e.g. BK-24681"
                      value={bookigIdNew}
                      onChange={(e) => setBookigIdNew(e.target.value)}
                    />
                    <button onClick={searchBooking}>Search</button>
                  </div>
                </div>

                {bookingData && (<>
                  <div className={styles.ResultHead}>
                    <span className={styles.ResultEyebrow}>Order</span>
                    <span className={styles.ResultTitle}>#{bookingData?.booking_id}</span>
                  </div>

                  <div className={styles.DetailGrid}>
                    <div className={styles.DetailCol}>
                      <p className={styles.DetailTitle}>Order details</p>
                      <p className={styles.DetailLine}>
                        <span>Booking ID</span>
                        <span className={styles.DetailLineValue}>{bookingData?.booking_id}</span>
                      </p>
                      <p className={styles.DetailLine}>
                        <span>Placed on</span>
                        <span className={styles.DetailLineValue}>{dayjs(bookingData?.created_at).format("DD MMM YYYY")}</span>
                      </p>
                      <p className={styles.DetailLine}>
                        <span>Contact No</span>
                        <span className={styles.DetailLineValue}>{bookingData?.pickup_details?.phone}</span>
                      </p>
                      {bookingData?.pickup_details?.alt_phones?.map((item2, idx) => (
                        <p className={styles.DetailLine} key={idx}>
                          <span>Alt Contact No</span>
                          <span className={styles.DetailLineValue}>{item2}</span>
                        </p>
                      ))}
                    </div>

                    <div className={styles.DetailCol}>
                      <p className={styles.DetailTitle}>Shipment tracking</p>

                      {meta && (
                        <div className={styles.StatusRow}>
                          <span className={styles.StatusLabel}>Status</span>
                          <span className={meta.cls}>{meta.label}</span>
                        </div>
                      )}

                      {statusNum !== -1 && (<>
                        <p className={styles.EstiTitle}>{statusNum === 7 ? "Delivered at" : "Estimated delivery date"}</p>
                        <p className={styles.TrasitDT}>
                          {bookingData?.estimated_delivery_date
                            ? dayjs(bookingData?.estimated_delivery_date).format("ddd, MMM DD")
                            : dayjs(bookingData?.booking_date).add(1, 'month').format("ddd, MMM DD")}
                        </p>
                      </>)}

                      {statusNum === -1 && (
                        <div className={styles.CancelledBanner}>This booking was cancelled.</div>
                      )}

                      {bookingData?.trackingDetails && (
                        <div className={styles.Stepper}>
                          {bookingData.trackingDetails.map((item, idx) => {
                            const isDone = !!item?.is_completed;
                            const isCurrent = idx === firstPendingIndex;
                            return (
                              <div className={styles.StepperItem} key={idx}>
                                <div className={`${styles.StepperLine} ${isDone ? styles.Done : ""}`} />
                                <div className={`${styles.StepperDot} ${isDone ? styles.Done : ""} ${isCurrent ? styles.Current : ""}`} />
                                <span className={`${styles.StepperLabel} ${isCurrent ? styles.Current : ""}`}>
                                  {item?.status_text}
                                </span>
                                {isDone && item?.datetime && (
                                  <span className={styles.StepperDate}>{dayjs(item.datetime).format("DD MMM")}</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </>)}

                {bookingData && bookingData?.products?.length > 0 && (
                  <div className={styles.StatFullSec}>
                    <div className={styles.ResultHead}>
                      <span className={styles.ResultEyebrow}>Items</span>
                      <span className={styles.ResultTitle}>Order contents</span>
                    </div>
                    <div className={styles.BookingTable}>
                      <div className={styles.TableHead}>
                        <span className={styles.TableHeadCell}>Pickup Id</span>
                        <span className={styles.TableHeadCell}>Item</span>
                        <span className={styles.TableHeadCell}>Estimated Value</span>
                        <span className={styles.TableHeadCell}>Status</span>
                      </div>
                      {bookingData.products.map((item, index) => {
                        const itemStatus = getItemStatusInfo(item);
                        return (
                        <div className={styles.TableRow} key={index}>
                          <span className={styles.TableCon} data-label="Pickup Id">{item?.item_id}</span>
                          <span className={styles.TableCon} data-label="Item">{item?.product_title}</span>
                          <span className={styles.TableCon} data-label="Estimated Value">
                            £{parseFloat(item?.subtotal)?.toFixed(2)}
                            {parseFloat(item.discount) > 0 && (
                              <>
                                <br />
                                <span style={{ fontSize: "11px" }}>(-£{parseFloat(item.discount).toFixed(2)})</span>
                              </>
                            )}
                          </span>
                          <span className={styles.TableCon} data-label="Status">
                            <span className={`${styles.ItemStatusBadge} ${itemStatus.className}`}>{itemStatus.label}</span>
                            {parseInt(item?.is_deliverred) === 1 && item?.delivered_date && (
                              <span className={styles.ItemStatusDate}>{dayjs(item.delivered_date).format("DD MMM YYYY")}</span>
                            )}
                          </span>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {bookingData?.delivery_completions?.length > 0 && (
                  <div className={styles.DeliveryRecordCard}>
                    <div className={styles.ResultHead}>
                      <span className={styles.ResultEyebrow}>Delivery record</span>
                      <span className={styles.ResultTitle}>Delivery groups</span>
                    </div>
                    <div className={styles.DeliveryGroupList}>
                      {bookingData.delivery_completions.map((group) => (
                        <div className={styles.DeliveryGroupBox} key={group.id}>
                          <div className={styles.DeliveryGroupTop}>
                            <span className={styles.DeliveryGroupDate}>{dayjs(group.delivered_date).format("ddd, DD MMM YYYY")}</span>
                            <span className={styles.DeliveryGroupCount}>{group.items?.length || 0} item{(group.items?.length || 0) !== 1 ? "s" : ""} delivered</span>
                          </div>
                          {group.items?.length > 0 && (
                            <ul className={styles.DeliveryGroupItems}>
                              {group.items.map((item) => <li key={item.id}>{item?.product_title || item?.item_id}</li>)}
                            </ul>
                          )}
                          {group.delivery_note && <p className={styles.DeliveryGroupNote}>“{group.delivery_note}”</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Snackbar
      open={snackbarOpen}
      autoHideDuration={6000}
      onClose={(e) => setSnackbarOpen(false)}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <Alert
        onClose={(e) => setSnackbarOpen(false)}
        severity={snackbarType}
        variant="filled"
        sx={{ width: "100%" }}
      >
        {snackbarMessage}
      </Alert>
    </Snackbar>
  </>);
}

export default withCustomerAuth(TrackBooking);
